import { z } from "zod";

import { logger } from "@/lib/logger";
import { SharePointSite } from "@/models/microsoft/CloudLocation";

const GRAPH_SITES_SEARCH_URL = "https://graph.microsoft.com/v1.0/sites";

/** Delegated Graph scope the app registration must hold for site search. */
export const SITES_READ_ALL_SCOPE = "Sites.Read.All";

const graphSitesResponseSchema = z.object({
  value: z.array(
    z.object({
      id: z.string(),
      displayName: z.string().optional(),
      name: z.string().optional(),
      webUrl: z.string(),
    })
  ),
});

export class SharePointSitesSearchError extends Error {
  constructor(
    readonly status: number,
    statusText: string
  ) {
    super(`Graph site search failed: ${status} ${statusText}`);
    this.name = "SharePointSitesSearchError";
  }

  /** 401/403: the tenant has not consented to Sites.Read.All (or the token lacks it). */
  get isAccessDenied(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * Turns what the user typed into a Graph site-search expression.
 *
 * Graph's `sites?search=` is a keyword search: "lod" finds nothing while
 * "lod*" finds "LODA test", and every word must match, so "LODA t" fails
 * until it becomes "LODA* t*". Each whitespace-separated term therefore gets
 * a trailing wildcard unless it already has one or is a quoted phrase.
 */
export const toGraphSiteSearchQuery = (query: string): string =>
  (query.match(/"[^"]*"|\S+/g) ?? [])
    .map((term) =>
      term.endsWith("*") || term.startsWith('"') ? term : `${term}*`
    )
    .join(" ");

/**
 * Searches the SharePoint sites the signed-in user can access.
 * Takes the access token as a parameter so this module stays MSAL-free.
 */
export const searchSharePointSites = async (
  query: string,
  accessToken: string
): Promise<SharePointSite[]> => {
  const url = `${GRAPH_SITES_SEARCH_URL}?search=${encodeURIComponent(toGraphSiteSearchQuery(query))}&$select=id,displayName,name,webUrl`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    const error = new SharePointSitesSearchError(
      response.status,
      response.statusText
    );
    const context = {
      operation: "searchSharePointSites",
      target: "graphApi",
      status: response.status,
      statusText: response.statusText,
      queryLength: query.length,
    };
    if (error.isAccessDenied) {
      // Expected until the customer tenant consents to Sites.Read.All.
      logger.warn(
        "Graph site search denied — Sites.Read.All not granted",
        context
      );
    } else {
      logger.error("Graph site search failed", { ...context, error });
    }
    throw error;
  }

  const parsed = graphSitesResponseSchema.parse(await response.json());
  return parsed.value.map((site) => ({
    webUrl: site.webUrl,
    displayName: site.displayName ?? site.name ?? site.webUrl,
  }));
};
